import { QoEAppLogger } from '../../logging/qoe/QoEAppLogger';
import { TVideoPlaybackStatsList } from '../types/TVideoPlaybackStatsList';
import { VideoPlaybackController } from '../VideoPlaybackController';

export class VideoPlaybackStats {
    private videoPlaybackController: VideoPlaybackController =
        VideoPlaybackController.getInstance();

    private frameRate: string;

    private makeVarient: string;

    private varients: string;

    private currentVarient: string;

    private varientEntry = new Set<string>();

    private playbackStats: TVideoPlaybackStatsList;

    public constructor() {
        this.frameRate = '';
        this.makeVarient = '';
        this.varients = '';
        this.currentVarient = '';
        this.playbackStats = {} as TVideoPlaybackStatsList;
    }

    private static getUserAgent(): string {
        const { userAgent } = navigator;
        if (userAgent === '') {
            return 'No';
        }
        return userAgent;
    }

    private getChannelCallSign(): string {
        const channelNumber = this.videoPlaybackController.getPlayable()?.channel?.channelNumber;
        const callSign = this.videoPlaybackController.getPlayable()?.channel?.callSign;
        return `${channelNumber} ${callSign}`;
    }

    private getVideoResolution(): string {
        const resolution = `${this.videoPlaybackController.getPlayerStats().height}p`;
        const variantGet = this.videoPlaybackController.getVideoVariants();

        variantGet.forEach((variant: any) => {
            if (`${variant.height}p` === resolution) {
                this.makeVarient = `{${variant.height}X${variant.width}@${variant.bandwidth / 1000} kbps}`;
                this.varientEntry.add(this.makeVarient);
                this.frameRate = variant.frameRate ?? '';
            } else {
                this.varientEntry.add(
                    `{${variant.height}X${variant.width}@ ${variant.bandwidth / 1000} kbps}`,
                );
            }
        });

        if (this.frameRate !== '') {
            this.frameRate = `,${this.frameRate}fps`;
        }

        return `${resolution}${this.frameRate}`;
    }

    private getVideoConnectionSpeed(): string {
        const connectionSpeedCheck: number =
            this.videoPlaybackController.getPlayerStats().estimatedBandwidth;
        const connectionSpeed: string = `${Math.round(connectionSpeedCheck / 1000)} kbps`;

        return connectionSpeed;
    }

    private getVideoBitRate(): string {
        const getBitrate: number = this.videoPlaybackController.getPlayerStats().streamBandwidth;
        const bitrateUsed: string = `${Math.round(getBitrate / 1000)} kbps`;

        return bitrateUsed;
    }

    private getVideoDroppedAndDecodeFrames(): string {
        const droppedAndDecodeFrames = `${this.videoPlaybackController.getPlayerStats().droppedFrames}/${
            this.videoPlaybackController.getPlayerStats().decodedFrames
        }`;

        return droppedAndDecodeFrames;
    }

    private getVideoCodecAndDrm(): string {
        const codecAndDrm = `${this.videoPlaybackController.getAudioVideoCodec()},
        ${this.videoPlaybackController.getDrmServerType()}`;

        return codecAndDrm;
    }

    private getVideoVarients(): string {
        this.varientEntry.forEach((item) => {
            if (item === this.makeVarient) {
                this.currentVarient = `{*${item}*}`;
            } else {
                this.varients += `{${item}} `;
            }
        });
        this.varients += this.currentVarient;

        return this.varients;
    }

    private getVideoStreamUrl(): string {
        return this.videoPlaybackController.getStreamingUrl();
    }

    private getVideoBufferAvailable(): string {
        const currentTime: number = this.videoPlaybackController.getPlayerCurrentTime() / 60;
        const endTime: number = this.videoPlaybackController.getSeekRange().end / 60;

        if (endTime > currentTime) {
            QoEAppLogger.mediaStats.videoBufferAvailable = endTime - currentTime;
        }
        const getBufferAvailable = Math.round(QoEAppLogger.mediaStats.videoBufferAvailable / 60);

        return `${getBufferAvailable} S`;
    }

    private getVideoAudioTrack(): string {
        return this.videoPlaybackController.getSelectedAudioTrack();
    }

    private getVideoTextTracks(): string {
        const ccTracks = this.videoPlaybackController.getTextTracks();
        const selectedCCTrack = this.videoPlaybackController.getSelectedTextTrack();
        let ccTracksList = '';

        if (ccTracks?.length) {
            ccTracksList = ccTracks
                .map((ccTrack) =>
                    ccTrack.text === selectedCCTrack ? `*${ccTrack.text}* ` : `${ccTrack.text} `,)
                .join('');
        }
        if (ccTracksList === '') {
            ccTracksList = 'No';
            return ccTracksList;
        }
        return `Yes, ${ccTracksList}`;
    }

    private getVideoPlaybackType(): string {
        if (this.videoPlaybackController.isLive()) {
            return 'Live';
        }
        if (this.videoPlaybackController.isStartOver()) {
            return 'Start Over';
        }
        if (this.videoPlaybackController.isCatchUp()) {
            return 'Catch Up';
        }
        if (this.videoPlaybackController.isNdvr()) {
            if (this.videoPlaybackController.isNdvrLive()) {
                return 'Live Recording';
            }
            if (this.videoPlaybackController.isNdvrRecorded()) {
                return 'Recorded';
            }
        }

        return 'Unknown';
    }

    public getVideoPlaybackStats(): TVideoPlaybackStatsList {
        this.playbackStats.channelCallSign = this.getChannelCallSign();
        this.playbackStats.resolutionRate = this.getVideoResolution();
        this.playbackStats.connectionSpeed = this.getVideoConnectionSpeed();
        this.playbackStats.biteRate = this.getVideoBitRate();
        this.playbackStats.droppedFrames = this.getVideoDroppedAndDecodeFrames();
        this.playbackStats.codecAndDrm = this.getVideoCodecAndDrm();
        this.playbackStats.varients = this.getVideoVarients();
        this.playbackStats.streamUrl = this.getVideoStreamUrl();
        this.playbackStats.bufferAvailable = this.getVideoBufferAvailable();
        this.playbackStats.audioTrack = this.getVideoAudioTrack();
        this.playbackStats.ccTracks = this.getVideoTextTracks();
        this.playbackStats.userAgent = VideoPlaybackStats.getUserAgent();
        this.playbackStats.videoPlaybackType = this.getVideoPlaybackType();
        return this.playbackStats;
    }
}
